import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Briefcase, ListChecks, Plus, Users } from 'lucide-react';
import {
  useActivities,
  useBillables,
  useClients,
  useCreateEntity,
  useDeleteEntity,
  useUpdateEntity
} from '@/hooks/useReference';
import { useBillingNouns } from '@/hooks/useSession';
import type { Activity, BillableParty, Client } from '@/types';
import { Screen } from '@m/ui/Screen';
import { Avatar, Group, Row } from '@m/ui/List';
import { BarButton, Button, Empty, SearchField, Segmented, SkeletonList } from '@m/ui/Controls';
import { SwipeRow } from '@m/ui/SwipeRow';
import { withUndo } from '@m/ui/undo';
import { FieldRow } from './clients/forms';
import { NameSheet } from './clients/NameSheet';
import { BillingPartySheet } from './clients/BillingPartySheet';
import { rateLabel, rateOf, useUnbilledByClient, wholeMoney } from './clients/useUnbilled';

type Kind = 'clients' | 'activities' | 'billing';

/**
 * S11. Clients, Activities and Billing parties share one tab: the three lists
 * a timesheet is made of. `?kind=` keeps the segment across back navigation.
 */
export function ClientsScreen() {
  const nouns = useBillingNouns();
  const [params, setParams] = useSearchParams();
  const kind = (params.get('kind') as Kind | null) ?? 'clients';
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);

  return (
    <Screen
      title={nouns.clients}
      actions={
        <BarButton aria-label="Add" onClick={() => setAdding(true)}>
          <Plus className="size-[26px]" strokeWidth={2.2} />
        </BarButton>
      }
    >
      <SearchField value={search} onChange={setSearch} />
      <Segmented
        value={kind}
        onChange={(k) => {
          setSearch('');
          setParams(k === 'clients' ? {} : { kind: k }, { replace: true });
        }}
        options={[
          { value: 'clients', label: nouns.clients },
          { value: 'activities', label: 'Activities' },
          { value: 'billing', label: nouns.payers }
        ]}
      />

      {kind === 'clients' && <ClientList search={search} onAdd={() => setAdding(true)} />}
      {kind === 'activities' && <ActivityList search={search} onAdd={() => setAdding(true)} />}
      {kind === 'billing' && <BillingList search={search} onAdd={() => setAdding(true)} />}

      {kind === 'clients' && <NewClientSheet key={String(adding)} open={adding} onClose={() => setAdding(false)} />}
      {kind === 'activities' && <NewActivitySheet key={String(adding)} open={adding} onClose={() => setAdding(false)} />}
      {kind === 'billing' && (
        <BillingPartySheet key={String(adding)} open={adding} party={null} initialName={search} onClose={() => setAdding(false)} />
      )}
    </Screen>
  );
}

function matches(name: string, search: string) {
  return !search.trim() || name.toLowerCase().includes(search.trim().toLowerCase());
}

function ClientList({ search, onAdd }: { search: string; onAdd: () => void }) {
  const nouns = useBillingNouns();
  const clients = useClients();
  const { byId, defaultRate } = useUnbilledByClient();

  const { active, inactive } = useMemo(() => {
    const list = (clients.data ?? [])
      .filter((c) => c.status !== 'archived' && matches(c.name, search))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { active: list.filter((c) => c.status === 'active'), inactive: list.filter((c) => c.status === 'inactive') };
  }, [clients.data, search]);

  if (clients.isPending) return <SkeletonList />;
  if (active.length + inactive.length === 0) {
    return (
      <Empty icon={<Users />} title={search ? 'No matches' : `No ${nouns.clients.toLowerCase()} yet`} body={search ? undefined : `Add a ${nouns.clientLower} to log time against.`}>
        {!search && <Button onClick={onAdd}>Add a {nouns.clientLower}</Button>}
      </Empty>
    );
  }

  const row = (c: Client) => {
    const u = byId.get(c.id);
    return (
      <Row
        key={c.id}
        to={`/clients/${c.id}`}
        chevron={false}
        leading={<Avatar id={c.id} name={c.name} />}
        title={c.name}
        subtitle={`${c.billable?.name ?? 'Bills itself'} · ${rateLabel(rateOf(c, defaultRate))}`}
        trailing={
          u && u.amount > 0 ? (
            <span className="tnum text-body font-medium">{wholeMoney(u.amount)}</span>
          ) : (
            <span className="text-body font-medium text-[var(--text-subtle)]">—</span>
          )
        }
      />
    );
  };

  const count = active.length;
  return (
    <>
      {active.length > 0 && (
        <Group header={`${count} ${count === 1 ? nouns.clientLower : nouns.clients.toLowerCase()}`} headerRight={<span className="tracking-[0.02em] uppercase">Unbilled</span>}>
          {active.map(row)}
        </Group>
      )}
      {inactive.length > 0 && <Group header="Inactive">{inactive.map(row)}</Group>}
    </>
  );
}

function ActivityList({ search, onAdd }: { search: string; onAdd: () => void }) {
  const activities = useActivities();
  const del = useDeleteEntity('activity');
  const update = useUpdateEntity('activity');
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Activity | null>(null);

  const list = (activities.data ?? [])
    .filter((a) => !hidden.has(a.id) && matches(a.name, search))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (activities.isPending) return <SkeletonList />;
  if (list.length === 0) {
    return (
      <Empty icon={<ListChecks />} title={search ? 'No matches' : 'No activities yet'} body={search ? undefined : 'Activities say what the time was for: research, a call, a draft.'}>
        {!search && <Button onClick={onAdd}>Add an activity</Button>}
      </Empty>
    );
  }

  return (
    <>
      <Group header={`${list.length} ${list.length === 1 ? 'activity' : 'activities'}`}>
        {list.map((a) => (
          <SwipeRow
            key={a.id}
            title={a.name}
            trailing={[
              {
                label: 'Delete',
                tone: 'danger',
                onSelect: () =>
                  withUndo({
                    message: `Deleted “${a.name}”`,
                    hide: () => setHidden((s) => new Set(s).add(a.id)),
                    restore: () =>
                      setHidden((s) => {
                        const n = new Set(s);
                        n.delete(a.id);
                        return n;
                      }),
                    commit: () => del.mutate({ id: a.id })
                  })
              }
            ]}
          >
            <Row title={a.name} onClick={() => setEditing(a)} chevron />
          </SwipeRow>
        ))}
      </Group>
      <NameSheet
        key={editing?.id ?? 'none'}
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Rename activity"
        initial={editing?.name ?? ''}
        onSave={(name) => editing && update.mutate({ id: editing.id, patch: { name } })}
      />
    </>
  );
}

function BillingList({ search, onAdd }: { search: string; onAdd: () => void }) {
  const nouns = useBillingNouns();
  const billables = useBillables();
  const [editing, setEditing] = useState<BillableParty | null>(null);

  const list = (billables.data ?? [])
    .filter((b) => matches(`${b.name} ${b.organization ?? ''}`, search))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (billables.isPending) return <SkeletonList />;
  if (list.length === 0) {
    return (
      <Empty icon={<Briefcase />} title={search ? 'No matches' : `No ${nouns.payers.toLowerCase()} yet`} body={search ? undefined : 'Who the invoice is addressed and emailed to.'}>
        {!search && <Button onClick={onAdd}>Add one</Button>}
      </Empty>
    );
  }

  return (
    <>
      <Group header={`${list.length} ${list.length === 1 ? nouns.payer.toLowerCase() : nouns.payers.toLowerCase()}`}>
        {list.map((b) => (
          <Row
            key={b.id}
            onClick={() => setEditing(b)}
            chevron
            leading={<Avatar id={b.id} name={b.name} />}
            title={b.name}
            subtitle={b.emails[0] ?? b.organization ?? 'No invoice email'}
          />
        ))}
      </Group>
      <BillingPartySheet key={editing?.id ?? 'none'} open={editing !== null} party={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function NewClientSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nouns = useBillingNouns();
  const create = useCreateEntity('client');
  const [rate, setRate] = useState('');
  return (
    <NameSheet
      open={open}
      onClose={onClose}
      title={`New ${nouns.clientLower}`}
      action="Add"
      footer={`Set the ${nouns.payer.toLowerCase()} from the ${nouns.clientLower}'s page.`}
      extra={() => (
        <div className="overflow-hidden rounded-2xl bg-[var(--surface)]">
          <FieldRow label="Hourly rate" inputMode="decimal" placeholder="Team default" value={rate} onChange={(e) => setRate(e.target.value)} />
        </div>
      )}
      onSave={(name) => create.mutate({ name, hourlyRate: Number(rate) > 0 ? Number(rate) : null })}
    />
  );
}

function NewActivitySheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateEntity('activity');
  return <NameSheet open={open} onClose={onClose} title="New activity" action="Add" onSave={(name) => create.mutate({ name })} />;
}
